import { useState, useCallback, useMemo } from 'react';

export interface UseSelectionReturn {
  /** Set of selected IDs */
  selectedIds: Set<string>;
  /** Last clicked ID (for shift-click range selection) */
  lastClickedId: string | null;
  /** Whether multiple items are selected */
  isMultiSelectMode: boolean;
  /** Array of selected IDs */
  selectedArray: string[];
  /** Count of selected items */
  selectionCount: number;
  /** Check if an item is selected */
  isSelected: (id: string) => boolean;
  /** Toggle a single item's selection */
  toggleSelection: (id: string) => void;
  /** Add item to selection */
  addToSelection: (id: string) => void;
  /** Remove item from selection */
  removeFromSelection: (id: string) => void;
  /** Select a range of items (for Shift+Click) */
  selectRange: (id: string, allIds: string[]) => void;
  /** Select a single item (clears others) */
  selectSingle: (id: string) => void;
  /** Clear all selections */
  clearSelection: () => void;
  /** Select all items from a list of IDs */
  selectAll: (allIds: string[]) => void;
  /** Set last clicked ID (for range selection anchor) */
  setLastClickedId: (id: string | null) => void;
  /** Set selection directly */
  setSelectedIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  /**
   * Handle click on an item with modifier key support
   * - Normal click: Select single item
   * - Cmd/Ctrl+Click: Toggle in selection
   * - Shift+Click: Range select
   */
  handleItemClick: (id: string, allIds: string[], e: React.MouseEvent) => void;
  /**
   * Handle checkbox toggle (always toggles, regardless of modifiers)
   */
  handleCheckChange: (id: string, checked: boolean) => void;
}

/**
 * Hook for managing multi-select functionality
 * Supports single select, toggle select (Cmd/Ctrl+Click), and range select (Shift+Click)
 */
export const useSelection = (): UseSelectionReturn => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lastClickedId, setLastClickedId] = useState<string | null>(null);

  const isMultiSelectMode = selectedIds.size > 1;
  const selectionCount = selectedIds.size;
  const selectedArray = useMemo(() => Array.from(selectedIds), [selectedIds]);

  const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds]);

  const toggleSelection = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    setLastClickedId(id);
  }, []);

  const addToSelection = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    setLastClickedId(id);
  }, []);

  const removeFromSelection = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const selectRange = useCallback(
    (id: string, allIds: string[]) => {
      if (!lastClickedId) {
        // No previous selection, just select this one
        setSelectedIds(new Set([id]));
        setLastClickedId(id);
        return;
      }

      const startIndex = allIds.indexOf(lastClickedId);
      const endIndex = allIds.indexOf(id);

      if (startIndex === -1 || endIndex === -1) {
        // One of the IDs not found, just select the clicked one
        setSelectedIds(new Set([id]));
        setLastClickedId(id);
        return;
      }

      const start = Math.min(startIndex, endIndex);
      const end = Math.max(startIndex, endIndex);
      const rangeIds = allIds.slice(start, end + 1);

      setSelectedIds(prev => {
        const next = new Set(prev);
        rangeIds.forEach(rangeId => next.add(rangeId));
        return next;
      });
      // Don't update lastClickedId for range selection
    },
    [lastClickedId]
  );

  const selectSingle = useCallback((id: string) => {
    setSelectedIds(new Set([id]));
    setLastClickedId(id);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setLastClickedId(null);
  }, []);

  const selectAll = useCallback((allIds: string[]) => {
    setSelectedIds(new Set(allIds));
  }, []);

  const handleItemClick = useCallback(
    (id: string, allIds: string[], e: React.MouseEvent) => {
      const isMeta = e.metaKey || e.ctrlKey;
      const isShift = e.shiftKey;

      if (isShift && lastClickedId) {
        // Shift+Click: Range select
        const startIdx = allIds.indexOf(lastClickedId);
        const endIdx = allIds.indexOf(id);

        if (startIdx !== -1 && endIdx !== -1) {
          const [from, to] = startIdx < endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
          const rangeIds = allIds.slice(from, to + 1);

          setSelectedIds(prev => {
            const newSet = new Set(prev);
            rangeIds.forEach(rangeId => newSet.add(rangeId));
            return newSet;
          });
        }
      } else if (isMeta) {
        // Cmd/Ctrl+Click: Toggle selection
        setSelectedIds(prev => {
          const newSet = new Set(prev);
          if (newSet.has(id)) {
            newSet.delete(id);
          } else {
            newSet.add(id);
          }
          return newSet;
        });
        setLastClickedId(id);
      } else {
        // Normal click: Single select
        setSelectedIds(new Set([id]));
        setLastClickedId(id);
      }
    },
    [lastClickedId]
  );

  const handleCheckChange = useCallback((id: string, checked: boolean) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  }, []);

  return {
    selectedIds,
    lastClickedId,
    isMultiSelectMode,
    selectedArray,
    selectionCount,
    isSelected,
    toggleSelection,
    addToSelection,
    removeFromSelection,
    selectRange,
    selectSingle,
    clearSelection,
    selectAll,
    setLastClickedId,
    setSelectedIds,
    handleItemClick,
    handleCheckChange,
  };
};
